import { useState, useEffect, useCallback, useTransition } from "react";
import { useQuery, useSubscription } from "urql";
import { TRADES_QUERY, TRADES_SUBSCRIPTION } from "../graphql/queries";

export const useTrades = (pairAddress, filters, pause = false) => {
  const [trades, setTrades] = useState([]);
  const [pageVariables, setPageVariables] = useState([
    { first: 50, after: "" },
  ]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalTrades, setTotalTrades] = useState(0);
  const [onTradeCallback, setOnTradeCallback] = useState(null);
  const [isPending, startTransition] = useTransition();

  // Query for trades with pagination
  const [{ data, fetching }] = useQuery({
    query: TRADES_QUERY,
    variables: {
      pageArgs: pageVariables[pageVariables.length - 1],
      filter: { ...filters, pair: pairAddress, networkId: 1 },
    },
    pause,
  });

  const [{ data: subscriptionData }] = useSubscription({
    query: TRADES_SUBSCRIPTION,
    variables: { pair: pairAddress },
    pause,
  });

  useEffect(() => {
    if (subscriptionData?.trades) {
      const newTrade = subscriptionData?.trades;
      startTransition(() => {
        setTrades((prev) => [newTrade, ...prev]);
        setTotalTrades((prev) => prev + 1);
      });
      // Call the onTrade callback if it exists
      if (onTradeCallback) {
        onTradeCallback(newTrade);
      }
    }
  }, [subscriptionData, onTradeCallback]);

  // Handle data updates
  useEffect(() => {
    if (data?.trades) {
      startTransition(() => {
        if (pageVariables.length === 1) {
          // Initial load
          setTrades(data?.trades.edges.map((edge) => edge.node));
        } else {
          // Append new trades for pagination
          setTrades((prev) => {
            const newTrades = data?.trades.edges.map((edge) => edge.node);
            const existingTradesMap = Object.fromEntries(
              prev.map((trade) => [trade.transactionHash, true])
            );

            return [
              ...prev,
              ...newTrades.filter(
                (trade) => !existingTradesMap[trade.transactionHash]
              ),
            ];
          });
        }
        setHasMore(data?.trades.pageInfo.hasNextPage);
        setIsLoadingMore(false);
        setTotalTrades(data?.trades.pageInfo.totalCount);
      });
    }
  }, [data, fetching, pageVariables.length]);

  // Reset pagination when filters or pair changes
  useEffect(() => {
    startTransition(() => {
      setPageVariables([{ first: 50, after: "" }]);
      setTrades([]);
      setHasMore(true);
      setTotalTrades(0);
    });
  }, [filters, pairAddress]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || fetching) return;

    setIsLoadingMore(true);
    const lastCursor =
      data?.trades?.edges[data?.trades?.edges.length - 1]?.cursor;

    if (lastCursor) {
      startTransition(() => {
        setPageVariables((prev) => [...prev, { first: 50, after: lastCursor }]);
      });
    }
  }, [hasMore, isLoadingMore, data, fetching]);

  const onTrade = useCallback((callback) => {
    setOnTradeCallback(() => callback);
  }, []);

  return {
    totalTrades,
    trades,
    loading: fetching,
    isLoadingMore: isLoadingMore || isPending,
    hasMore,
    loadMore,
    onTrade,
  };
};
